import * as React from 'react';

import initBem from 'js/lib/bem';

import { ApiButton } from '@coursera/coursera-ui';

import { AssignmentPresentationGrader } from 'bundles/compound-assessments/components/api/AssignmentPresentation';
import type {
  SubmissionPrompt,
  SubmissionResponseContainer,
} from 'bundles/compound-assessments/components/api/types/CompoundAssessmentsForm';
import { getGitStatuses } from 'bundles/compound-assessments/components/form-parts/git/utils/GitUtils';
import type { AssignmentRole } from 'bundles/compound-assessments/types/Roles';
import GitRepoLauncher from 'bundles/git/components/repo-launcher/GitRepoLauncher';
import { assignmentRoleToLaunchRole } from 'bundles/git/utils/GitIntegrationsUtils';

import _t from 'i18n!nls/compound-assessments';

const bem = initBem('GitFormPart');

type Props = {
  courseId?: string;
  itemId?: string;
  prompt: SubmissionPrompt;
  response: SubmissionResponseContainer;
  userId?: number;
  role?: AssignmentRole;
};

const LoadingButton = () => (
  <ApiButton
    apiStatus="API_IN_PROGRESS"
    apiStatusAttributesConfig={{
      label: {
        API_IN_PROGRESS: _t('Loading assignment...'),
      },
    }}
  />
);

const PreparingButton = () => (
  <ApiButton
    apiStatus="API_IN_PROGRESS"
    apiStatusAttributesConfig={{
      label: {
        API_IN_PROGRESS: _t('Preparing...'),
      },
    }}
  />
);

const FailedButton = () => (
  <ApiButton
    apiStatus="API_ERROR"
    apiStatusAttributesConfig={{
      label: {
        API_ERROR: _t('Initialization failed'),
      },
    }}
  />
);

const MissingRepositoryIdButton = () => (
  <ApiButton
    apiStatus="API_ERROR"
    apiStatusAttributesConfig={{
      label: {
        API_ERROR: _t('Initialization failed. Please contact support.'),
      },
    }}
  />
);

const Git = ({ courseId, itemId, prompt, response, userId, role }: Props) => {
  const {
    submissionPartSchema: { submissionPartSchemaDetails },
  } = prompt;

  const { submissionPartResponse } = response.definition;

  // We know we're all Git after this (and TypeScript knows it too).
  if (
    submissionPartSchemaDetails.typeName !== 'gitSchema' ||
    submissionPartResponse.typeName !== 'gitResponse' ||
    !courseId ||
    !itemId ||
    !userId
  ) {
    return null;
  }

  const { repositoryId, commitHash } = submissionPartResponse.definition;
  const launchRole = assignmentRoleToLaunchRole(role);

  return (
    <AssignmentPresentationGrader courseId={courseId} itemId={itemId} userId={userId}>
      {(assignment) => {
        if (!assignment) {
          return <LoadingButton />;
        }

        const { isGitInitializationFailed, isGitInitializationInProgress } = getGitStatuses(assignment);

        if (isGitInitializationInProgress) {
          return <PreparingButton />;
        } else if (isGitInitializationFailed) {
          return <FailedButton />;
        } else if (!repositoryId) {
          return <MissingRepositoryIdButton />;
        }

        return (
          <div className={bem()}>
            <div className={bem('prompt')}>
              <GitRepoLauncher
                courseId={courseId}
                itemId={itemId}
                launchRole={launchRole}
                learnerUserId={userId}
                repositoryId={repositoryId}
                commitHash={commitHash}
              />
            </div>
          </div>
        );
      }}
    </AssignmentPresentationGrader>
  );
};

export default Git;
